import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  ScrollView,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { invitesApi, referralsApi } from "@/api";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import { referralStrings as S } from "@/i18n/features/referral.i18n";
import { notify } from "@/lib/dialog";
import { shareText } from "@/lib/share-text";
import { useAuthStore } from "@/state/auth-store";
import type { InviteView } from "@/types";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(i18n.language, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function InviteScreen() {
  useTranslation();
  const router = useRouter();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const user = useAuthStore((s) => s.user);

  const [view, setView] = useState<InviteView | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  /** A mint is in flight. Guards the double-tap that would spend two slots. */
  const [sharingCue, setSharingCue] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(false);
      setView(await referralsApi.me());
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      await load();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const onShare = useCallback(async () => {
    if (!view) return;
    try {
      await shareText({
        message: i18n.t(S.shareMessage, {
          code: view.code,
          url: view.inviteUrl,
        }),
      });
    } catch {
      // User dismissed the share sheet — nothing to do.
    }
  }, [view]);

  /**
   * Share a link that also asks to talk.
   *
   * A SECOND button rather than a switch beside the first, and the same
   * reasoning as the group join sheet: a toggle beside a Share button inherits
   * whatever it was last left at, and its default silently becomes the answer
   * for everybody who did not read it. Here that would mean sending a stranger
   * a cue request because a switch was still on from last week. Two labelled
   * buttons make the intent the act of sharing.
   *
   * Minted HERE, at the moment of sharing, and never when the screen opens.
   * Every mint spends a slot against the daily cap; a screen that minted on
   * mount would burn the whole allowance for somebody who opened it repeatedly
   * and shared nothing.
   *
   * The link is not the referral code. An envelope is single-use, expires in
   * seven days, and carries the intent — because "come and talk to me" is a
   * thing you say to one person on one day, and it has to reach the friend who
   * already has the app, whom referral attribution refuses by design.
   */
  const onShareWithCue = useCallback(async () => {
    if (sharingCue) return;
    setSharingCue(true);
    try {
      const invite = await invitesApi.mint(true);
      await shareText({
        message: i18n.t(S.shareWithCueMessage, { url: invite.url }),
      });
    } catch (err) {
      // Two quite different failures land here and only one is worth a word.
      // A dismissed share sheet rejects with nothing to say; a refused mint —
      // the daily cap, a switched-off account — has the server's own message,
      // and the person is owed it rather than a silent no-op.
      if (err instanceof Error && err.message) {
        notify(i18n.t(S.shareFailed), err.message);
      }
    } finally {
      setSharingCue(false);
    }
  }, [sharingCue]);

  const close = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace("/(tabs)/profile" as never);
  }, [router]);

  // `Date.now()` read during render made this component impure: the same props
  // and state could produce two different outputs, which is exactly what the
  // compiler cannot memoize safely. Reading the clock once, when the screen
  // opens, makes render a function of its inputs again.
  //
  // Nothing is lost. The old expression was only re-evaluated when something
  // else re-rendered this screen, so a grant expiring while the screen sat open
  // was never reflected live — it was re-read at an arbitrary moment instead of
  // a defined one. Reopening the screen re-reads it.
  const [openedAt] = useState(() => Date.now());

  const grantActive =
    user?.plusGrantUntil && new Date(user.plusGrantUntil).getTime() > openedAt
      ? user.plusGrantUntil
      : null;

  const capReached =
    view != null && view.rewardedThisMonth >= view.monthlyCap;

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style="auto" />

      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("common.back")}
          onPress={close}
          hitSlop={10}
          style={({ pressed }) => [styles.iconButton, pressed && styles.pressed]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>
        <Text style={styles.topBarTitle}>{i18n.t(S.title)}</Text>
        <View style={styles.headerSpacer} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={c.tint} />
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={styles.content}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={c.tint}
              // Android was entirely unstyled here: default white plate and
              // default arc on a themed page. Same treatment as the inbox —
              // invisible plate, tinted arc.
              colors={[c.tint]}
              progressBackgroundColor={c.background}
            />
          }
        >
          <View style={styles.inner}>
          {grantActive ? (
            <View style={styles.rewardBanner}>
              <Ionicons name="sparkles" size={16} color={c.onTint} />
              <Text style={styles.rewardText}>
                {i18n.t(S.rewardActive, { date: formatDate(grantActive) })}
              </Text>
            </View>
          ) : null}

          <Text style={styles.subtitle}>{i18n.t(S.subtitle)}</Text>

          {/* The invites list used to be the only place a failed load was
              reported. It is gone, so the error moves next to the thing that
              is actually missing when it fires: without `view` the code below
              renders as an em dash, and an unexplained em dash is a silent
              failure rather than a small one. */}
          {error ? (
            <Text style={styles.errorText}>{i18n.t(S.loadError)}</Text>
          ) : null}

          {/* Code + share */}
          <View style={styles.codeCard}>
            <Text style={styles.codeLabel}>{i18n.t(S.yourCode)}</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={i18n.t(S.share)}
              onPress={onShare}
            >
              <Text style={styles.code} selectable>
                {view?.code ?? "—"}
              </Text>
            </Pressable>
            {/*
              Side by side, and equal width.
              
              Stacked, the second button read as a lesser afterthought to the
              first — and it is not: they are two ways of doing the same thing,
              and which one somebody picks is the entire point of the feature.
              A row puts them at the same level and lets the shape carry that.
              
              `alignItems: "stretch"` on the row rather than a fixed height,
              because the labels translate to very different lengths — "Ask to
              talk" against "Pedir para conversar" — and one wrapping to two
              lines must take the other with it rather than leaving a pair of
              mismatched boxes.
            */}
            <View style={styles.shareRow}>
              {/*
                Label = what is DRAWN; hint = what it does.
                
                These carried the long forms as their `accessibilityLabel` while
                showing the short ones, which breaks WCAG 2.5.3 "Label in Name":
                a voice-control user saying the words on the button ("tap Invite
                and send a Cue") does not match an accessible name that says
                something else, so the control is unreachable by voice. The
                mismatch was harmless while the visible text was a substring of
                the label and stopped being so the moment the copy changed —
                which is the failure mode, since nothing about a copy edit looks
                like an accessibility change.
                
                The long forms move to `accessibilityHint`, which is the API for
                "what happens if you activate this" and where they earn their
                place: "Cue" is product jargon, and "invite and ask to talk" is
                what it means.
              */}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={i18n.t(S.shareShort)}
                accessibilityHint={i18n.t(S.share)}
                onPress={onShare}
                disabled={!view}
                style={({ pressed }) => [
                  styles.shareBtn,
                  pressed && styles.pressed,
                ]}
              >
                <Text style={styles.shareBtnText} numberOfLines={2}>
                  {i18n.t(S.shareShort)}
                </Text>
              </Pressable>

              <Pressable
                accessibilityRole="button"
                accessibilityLabel={i18n.t(S.shareWithCueShort)}
                accessibilityHint={i18n.t(S.shareWithCue)}
                onPress={() => void onShareWithCue()}
                disabled={sharingCue}
                style={({ pressed }) => [
                  styles.shareCueBtn,
                  (pressed || sharingCue) && styles.pressed,
                ]}
              >
                {/*
                  The spinner REPLACES the label rather than sitting beside it.
                  
                  It used to take the icon's place, so removing the icon left it
                  nowhere to go: beside the text it would widen the content
                  mid-tap and shove a two-line label around. Swapping it for the
                  label costs no width at all, because the button's size comes
                  from `flex: 1` and not from what is inside it.
                  
                  It stays, rather than going with the icons. Minting hits the
                  network before the share sheet can open, and this is the only
                  thing that says the tap registered.

                  `onTint`, not `tint`: it sits INSIDE the filled button now
                  that the emphasis is swapped, and a tint-coloured indicator
                  on a tint fill is invisible.
                */}
                {sharingCue ? (
                  <ActivityIndicator color={c.onTint} size="small" />
                ) : (
                  <Text style={styles.shareCueBtnText} numberOfLines={2}>
                    {i18n.t(S.shareWithCueShort)}
                  </Text>
                )}
              </Pressable>
            </View>
            <Text style={styles.shareCueHint}>
              {i18n.t(S.shareWithCueHint)}
            </Text>
          </View>

          {/* How it works */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{i18n.t(S.howItWorks)}</Text>
            {[S.step1, S.step2, S.step3].map((key, idx) => (
              <View key={key} style={styles.step}>
                <View style={styles.stepDot}>
                  <Text style={styles.stepNum}>{idx + 1}</Text>
                </View>
                <Text style={styles.stepText}>{i18n.t(key)}</Text>
              </View>
            ))}
            <Text style={capReached ? styles.capReached : styles.capNote}>
              {i18n.t(capReached ? S.capReached : S.capNote)}
            </Text>
          </View>
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

function createStyles(c: ThemeColors) {
  return {
    safe: { flex: 1, backgroundColor: c.background } as const,
    center: { flex: 1, alignItems: "center", justifyContent: "center" } as const,
    topBar: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: Spacing.md,
      paddingVertical: Spacing.sm,
    } as const,
    iconButton: { padding: Spacing.xs } as const,
    pressed: { opacity: 0.6 } as const,
    topBarTitle: {
      flex: 1,
      textAlign: "center",
      fontSize: 17,
      fontWeight: "700",
      color: c.text,
    } as const,
    headerSpacer: { width: 31 } as const,
    content: {
      padding: Spacing.lg,
      paddingBottom: Spacing.xxxl,
      alignItems: "center",
    } as const,
    // Keeps the dashboard a compact centered column on wide screens (tablet/web)
    // instead of stretching edge-to-edge; a no-op on phones narrower than the cap.
    inner: { width: "100%", maxWidth: 460, gap: Spacing.lg } as const,
    subtitle: { fontSize: 15, lineHeight: 21, color: c.textMuted } as const,
    rewardBanner: {
      flexDirection: "row",
      alignItems: "center",
      gap: Spacing.sm,
      backgroundColor: c.tint,
      borderRadius: Radius.md,
      padding: Spacing.md,
    } as const,
    rewardText: { flex: 1, color: c.onTint, fontWeight: "600", fontSize: 14 } as const,
    codeCard: {
      backgroundColor: c.surface,
      borderRadius: Radius.lg,
      borderWidth: 1,
      borderColor: c.border,
      padding: Spacing.xl,
      alignItems: "center",
      gap: Spacing.md,
    } as const,
    codeLabel: {
      fontSize: 12,
      fontWeight: "600",
      color: c.textMuted,
      textTransform: "uppercase",
      letterSpacing: 1,
    } as const,
    code: {
      fontSize: 34,
      fontWeight: "800",
      letterSpacing: 6,
      color: c.text,
    } as const,
    /**
     * The two share actions, in one row.
     *
     * `alignSelf: "stretch"` because `codeCard` centres its children, so
     * without it the row shrink-wraps to its content and the two buttons stop
     * being half the card each.
     */
    shareRow: {
      flexDirection: "row",
      alignItems: "stretch",
      alignSelf: "stretch",
      gap: Spacing.sm,
    } as const,
    /**
     * The plain share, now the OUTLINE of the pair (emphasis swapped
     * deliberately — the fill used to sit here).
     *
     * Passing the app along is the ordinary act; asking a specific person to
     * talk is the one that actually starts a conversation, so the fill went to
     * it. An outline still reads as a real, tappable choice — it is not
     * disabled and not hidden — it just stops competing with the button we
     * would rather someone pressed.
     */
    shareBtn: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      // No `gap`: it separated an icon from a label, and there is one child now.
      borderRadius: Radius.pill,
      borderWidth: 1,
      borderColor: c.tint,
      paddingVertical: Spacing.md,
      // Was `Spacing.xl`, which is generous on a full-width button and is most
      // of the available room on a half-width one. The row's own gap does the
      // separating now.
      paddingHorizontal: Spacing.sm,
    } as const,
    shareBtnText: {
      color: c.tint,
      fontWeight: "700",
      fontSize: 15,
      // Both are needed together. Without `flexShrink` the text lays out at its
      // full intrinsic width and pushes past the button's own border — which is
      // exactly what the outline button did before it had either, because it
      // also had no horizontal padding to give.
      flexShrink: 1,
      textAlign: "center",
    } as const,
    /**
     * The pointed share, and now the FILLED one of the pair.
     *
     * Emphasis was the other way round and was swapped deliberately. Sharing
     * the app puts a stranger in a list; sending a Cue with the invite puts a
     * named request in someone's hands, which is the act this screen exists to
     * produce. The fill follows the outcome we want rather than the milder
     * option.
     *
     * `borderWidth` is kept with the border set to the fill colour so both
     * buttons lay out at exactly the same height — dropping it would leave the
     * outline sibling 2px taller and the row visibly uneven.
     */
    shareCueBtn: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: c.tint,
      borderRadius: Radius.pill,
      borderWidth: 1,
      borderColor: c.tint,
      paddingVertical: Spacing.md,
      paddingHorizontal: Spacing.sm,
    } as const,
    shareCueBtnText: {
      color: c.onTint,
      fontWeight: "700",
      fontSize: 15,
      flexShrink: 1,
      textAlign: "center",
    } as const,
    shareCueHint: {
      color: c.textMuted,
      fontSize: 12,
      lineHeight: 17,
      // No `marginTop`. It sat directly under the outline button when the two
      // were stacked; now it is a sibling of the row inside `codeCard`, whose
      // own `gap` already separates them — keeping both stacked one on top of
      // the other and pushed the hint adrift from the buttons it explains.
      textAlign: "center",
    } as const,
    section: {
      backgroundColor: c.surface,
      borderRadius: Radius.lg,
      borderWidth: 1,
      borderColor: c.border,
      padding: Spacing.lg,
      gap: Spacing.md,
    } as const,
    sectionTitle: { fontSize: 16, fontWeight: "700", color: c.text } as const,
    step: { flexDirection: "row", alignItems: "flex-start", gap: Spacing.md } as const,
    stepDot: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: c.surfaceAlt,
      alignItems: "center",
      justifyContent: "center",
    } as const,
    stepNum: { fontSize: 13, fontWeight: "700", color: c.tint } as const,
    stepText: { flex: 1, fontSize: 14, lineHeight: 20, color: c.text } as const,
    capNote: {
      fontSize: 13,
      lineHeight: 19,
      color: c.textMuted,
      marginTop: Spacing.xs,
    } as const,
    capReached: {
      fontSize: 13,
      lineHeight: 19,
      color: c.tint,
      fontWeight: "600",
      marginTop: Spacing.xs,
    } as const,
    errorText: { fontSize: 14, color: c.textMuted } as const,
  };
}
